import React, { useState } from 'react';
import { Paper, Tabs, Tab, TabPanel, Divider } from '@kambi/spark';

import classes from '../../../App.module.css';
import CodeBlock from 'components/layout/code-block/CodeBlock';
import OddsMap from '../jsonExamples/OddsMap';
import OddsFormat from '../jsonExamples/OddsFormat';
import FractionalAsDecimals from '../jsonExamples/FractionalAsDecimals';
import OddsData from '../jsonExamples/OddsData';
import { UtilsTypes } from 'constants/utilsTypes';
import DecimalOdds from '../jsonExamples/DecimalOdds';

const FormatOddsTabDescription = () => {
    return (
        <>
            <div style={{ marginTop: '10px' }}>
                <b>oddsData</b> can be either an object or a number. If it's a
                number it should represent the value of the rawOdds. If it's an
                object it accept two different properties: startingPrice and
                odds. startingPrice, if set, tells the function to return the
                translation for starting price while the odds property should
                have the rawOdds as a number. Try out different values of
                oddsData in the input below
            </div>
            <Divider />
            <CodeBlock
                title={OddsData.title}
                expectedOutput={OddsData.expectedOutput}
                initialDisplayedCode={OddsData.initialDisplayedCode}
                moreCode={OddsData.moreCode}
            />
        </>
    );
};

const DecimalOddsTabDescription = () => {
    return (
        <>
            <div style={{ marginTop: '10px' }}>
                <b>decimalOdds</b> is a required property for getOddsTo2Decimals
                function and it should be a number. Try out different
                decimalOdds values in the input below
            </div>
            <Divider />
            <CodeBlock
                title={DecimalOdds.title}
                expectedOutput={DecimalOdds.expectedOutput}
                initialDisplayedCode={DecimalOdds.initialDisplayedCode}
                moreCode={DecimalOdds.moreCode}
            />
        </>
    );
};

const OddsUtilsTabs = ({ type }) => {
    const [value, setValue] = useState(0);

    const handleChange = (_, newValue) => {
        setValue(newValue);
    };

    const FractionalAsDecimalsByType = FractionalAsDecimals(type);
    const OddsFormatByType = OddsFormat(type);
    const OddsMapByType = OddsMap(type);

    return (
        <div className={classes.Tabs}>
            <Paper>
                <Tabs
                    kind="inverted"
                    value={value}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {type === UtilsTypes.FORMAT_ODDS ? (
                        <Tab label="Odds Data" />
                    ) : undefined}
                    <Tab label="Need Fractionals As Decimals" />
                    <Tab label="Odds Format" />
                    <Tab label="Odds Map" />
                </Tabs>
            </Paper>
            <TabPanel index={0} value={value} className={classes.TabPanel}>
                {type === UtilsTypes.FORMAT_ODDS ? (
                    <FormatOddsTabDescription />
                ) : undefined}
                {type === UtilsTypes.GET_ODDS_TO_DECIMALS ? (
                    <DecimalOddsTabDescription />
                ) : undefined}
            </TabPanel>
            <TabPanel index={1} value={value} className={classes.TabPanel}>
                <div style={{ marginTop: '10px' }}>
                    <b>needFractionalsAsDecimals</b> is a boolean that tells the
                    function to ignore the OddsMap and return the (oddsData/1000
                    - 1)/1. It works only for fractional odds format and it's a
                    required property. Try out the boolean value for the
                    needFractionalsAsDecimals in the input below
                </div>
                <Divider />
                <CodeBlock
                    title={FractionalAsDecimalsByType.title}
                    expectedOutput={FractionalAsDecimalsByType.expectedOutput}
                    initialDisplayedCode={
                        FractionalAsDecimalsByType.initialDisplayedCode
                    }
                    moreCode={FractionalAsDecimalsByType.moreCode}
                />
            </TabPanel>
            <TabPanel index={2} value={value} className={classes.TabPanel}>
                <div style={{ marginTop: '10px' }}>
                    <b>oddsFormat</b> is a string property and it's required.
                    Current accepted values are: "decimal", "american",
                    "fractional". You can test in the input below the different
                    values for oddsFormat
                </div>
                <Divider />
                <CodeBlock
                    title={OddsFormatByType.title}
                    expectedOutput={OddsFormatByType.expectedOutput}
                    initialDisplayedCode={OddsFormatByType.initialDisplayedCode}
                    moreCode={OddsFormatByType.moreCode}
                />
            </TabPanel>
            <TabPanel index={3} value={value} className={classes.TabPanel}>
                <div style={{ marginTop: '10px' }}>
                    <div>
                        <b>OddsMap</b>: An odds conversion table that allow
                        client side odds conversions to american or fractional
                        format. This is a required property. The Kambi client
                        uses the map from "/offering/oddsLadder" endpoint of the
                        offering API OddsMap should be an object with keys the
                        name of the format in which you want to convert the
                        rawOdds. Possible values:
                    </div>
                    <div>
                        <ul>
                            <li>decimal</li>
                            <li>american</li>
                            <li>fractional</li>
                        </ul>
                    </div>
                    <div>
                        The values for these keys should be an array containing
                        arrays of pairs [rawOdd, fractional/american/decimal
                        value] where <b>rawOdd</b> is the value coming from the
                        API and the next element of the array is the
                        corresponding value in{' '}
                        <b>fractional/american/decimal format</b>
                        <div>
                            Try out the different formats for the OddsMap in the
                            input below
                        </div>
                    </div>
                </div>
                <Divider />
                <CodeBlock
                    title={OddsMapByType.title}
                    expectedOutput={OddsMapByType.expectedOutput}
                    initialDisplayedCode={OddsMapByType.initialDisplayedCode}
                    moreCode={OddsMapByType.moreCode}
                />
            </TabPanel>
        </div>
    );
};

export default OddsUtilsTabs;
